import { Translations } from "ui/Scripts/source/models/translations";
import { ICodeNameUrl, CodeNameUrl } from "../common/codeNameUrl";
import { Base } from "ui/Scripts/source/framework/base";
import { ITypedOption, TypedOption } from "../common/typedOption";
import { userParameterShowPageNavigation } from "ui/Scripts/source/models/consts";
import { OptionDataType } from "ui/Scripts/source/models/enums";
import { CompanyMenuItem, ICompanyMenuItem } from "ui/Scripts/source/models/company/companyMenuItem";

export interface ISessionState {
    refreshTime: number;
    id: string;
    code: string;
    name: string;
    image: string;
    companyId: string;
    companyName: string;
    companyLogo: string;
    companyLogoSquare: string;
    culture: string;
    isCompanyAdmin: boolean;
    isCompanyWhistleBlowAdmin: boolean;
    isSuperAdmin: boolean;
    canUseMessages: boolean;
    canUseDocuments: boolean;
    canUseEvents: boolean;
    canUseTasks: boolean;
    canUseRecords: boolean;
    canUseOwnRecord: boolean;
    canUseCompanyGroups: boolean;
    canUseCompanies: boolean;
    canUseUserGroups: boolean;
    canUseUsers: boolean;
    canUseLogs: boolean;
    canUseInstantMessages: boolean;
    canManageInstantMessagePermissions: boolean;
    canUseWhistleBlow: boolean;
    canUseCalendarTasks: boolean;
    canUseReports: boolean;
    canUseRisks: boolean;
    allowedCompanies: ICompanyMenuItem[];
    intraModules: ICodeNameUrl[];
    intraRestModules: ICodeNameUrl[];
    externalSystems: ICodeNameUrl[];
    userParameters: ITypedOption[];

    canUseSystem(): boolean;
    canExternalSystems(): boolean;
    isExpired(): boolean;
    getShowPageNavigation(): boolean;
}

export class SessionState implements ISessionState {
    refreshTime: number;
    id: string;
    code: string;
    name: string;
    image: string;
    companyId: string;
    companyName: string;
    companyLogo: string;
    companyLogoSquare: string;
    culture: string;
    isCompanyAdmin: boolean;
    isCompanyWhistleBlowAdmin: boolean;
    isSuperAdmin: boolean;
    canUseMessages: boolean;
    canUseDocuments: boolean;
    canUseEvents: boolean;
    canUseTasks: boolean;
    canUseRecords: boolean;
    canUseOwnRecord: boolean;
    canUseCompanyGroups: boolean;
    canUseCompanies: boolean;
    canUseUserGroups: boolean;
    canUseUsers: boolean;
    canUseLogs: boolean;
    canUseInstantMessages: boolean;
    canManageInstantMessagePermissions: boolean;
    canUseWhistleBlow: boolean;
    canUseCalendarTasks: boolean;
    canUseReports: boolean;
    canUseRisks: boolean;
    allowedCompanies: CompanyMenuItem[];
    intraModules: CodeNameUrl[];
    intraRestModules: CodeNameUrl[];
    externalSystems: CodeNameUrl[];
    userParameters: TypedOption[];

    constructor();
    constructor(obj: ISessionState);
    constructor(obj?: any) {
        this.refreshTime = obj && obj.refreshTime || 0;
        this.id = obj && obj.id || "";
        this.code = obj && obj.code || "";
        this.name = obj && obj.name || "";
        this.image = obj && obj.image || "";
        this.companyId = obj && obj.companyId || "";
        this.companyName = obj && obj.companyName || "";
        this.companyLogo = obj && obj.companyLogo || "";
        this.companyLogoSquare = obj && obj.companyLogoSquare || "";
        this.culture = obj && obj.culture || "";
        this.isCompanyAdmin = obj && obj.isCompanyAdmin || false;
        this.isCompanyWhistleBlowAdmin = obj && obj.isCompanyWhistleBlowAdmin || false;
        this.isSuperAdmin = obj && obj.isSuperAdmin || false;
        this.canUseMessages = obj && obj.canUseMessages || false;
        this.canUseDocuments = obj && obj.canUseDocuments || false;
        this.canUseEvents = obj && obj.canUseEvents || false;
        this.canUseTasks = obj && obj.canUseTasks || false;
        this.canUseRecords = obj && obj.canUseRecords || false;
        this.canUseOwnRecord = obj && obj.canUseOwnRecord || false;
        this.canUseCompanyGroups = obj && obj.canUseCompanyGroups || false;
        this.canUseCompanies = obj && obj.canUseCompanies || false;
        this.canUseUserGroups = obj && obj.canUseUserGroups || false;
        this.canUseUsers = obj && obj.canUseUsers || false;
        this.canUseLogs = obj && obj.canUseLogs || false;
        this.canUseInstantMessages = obj && obj.canUseInstantMessages || false;
        this.canManageInstantMessagePermissions = obj && obj.canManageInstantMessagePermissions || false;
        this.canUseWhistleBlow = obj && obj.canUseWhistleBlow || false;
        this.canUseCalendarTasks = obj && obj.canUseCalendarTasks || false;
        this.canUseReports = obj && obj.canUseReports || false;
        this.canUseRisks = obj && obj.canUseRisks || false;
        this.allowedCompanies = Base.getTypedArray(obj ? obj.allowedCompanies : null, CompanyMenuItem);
        this.intraModules = Base.getTypedArray(obj ? obj.intraModules : null, CodeNameUrl);
        this.intraRestModules = Base.getTypedArray(obj ? obj.intraRestModules : null, CodeNameUrl);
        this.externalSystems = Base.getTypedArray(obj ? obj.externalSystems : null, CodeNameUrl);
        this.userParameters = Base.getTypedArray(obj ? obj.userParameters : null, TypedOption);
    }
    
    canUseSystem(): boolean {
        return this.canUseMessages ||
            this.canUseDocuments ||
            this.canUseEvents ||
            this.canUseTasks ||
            this.canUseRecords ||
            this.canUseOwnRecord ||
            this.canUseCompanyGroups ||
            this.canUseCompanies ||
            this.canUseUserGroups ||
            this.canUseUsers ||
            this.canUseLogs ||
            this.canUseInstantMessages ||
            this.canUseWhistleBlow ||
            this.canUseCalendarTasks ||
            this.canUseReports ||
            this.canUseRisks;
    }
    
    canExternalSystems(): boolean {
        return this.intraModules.length > 0 || this.intraRestModules.length > 0 || this.externalSystems.length > 0;
    }

    isExpired(): boolean {
        const currentTime = (new Date()).getTime();
        return currentTime >= new Date(this.refreshTime).addMinutes(appConfig.authenticationTimeout).getTime();
    }

    setUserParameters(newUserParameters: TypedOption[]) {
        const userParameters = this.userParameters.slice(0);
        for (const newParameter of newUserParameters) {
            const oldParameter = userParameters.find(i => i.code.toLowerCase() === newParameter.code.toLowerCase());
            if (oldParameter) {
                oldParameter.value = newParameter.value;
            } else {
                userParameters.push(newParameter);
            }
        }
        this.userParameters = userParameters;
    }

    getShowPageNavigation(): boolean {
        return TypedOption.getBooleanValue(this.userParameters, userParameterShowPageNavigation, false);
    }

    static setShowPageNavigation(value: boolean, onSaveUserParameters: (parameters: ITypedOption[]) => void) {
        onSaveUserParameters([TypedOption.createTypedOption(userParameterShowPageNavigation, OptionDataType.Boolean, value ? "1" : "0")]);
    }

    static initialize(): ISessionState {
        if (appConfig.sessionState) {
            const result = new SessionState(JSON.parse(appConfig.sessionState));
            result.refreshTime = (new Date()).getTime();
            result.culture = appConfig.culture;
            Translations.setCulture(appConfig.culture);
            return result;
        }
        return null;
    }
}